/**
 * PurchaseCreditsBundleModal - Increase planned usage.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  Slider,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconArrowRight,
  IconLibraryPlus,
  IconRosetteDiscountCheckFilled,
} from "@tabler/icons-react";
import type { ContractCommitmentBillingDetails } from "@/utils/billing/types";
import {
  getCreditBundlePricingContext,
  type CreditPricingTierContext,
  purchaseCreditBundle,
} from "../api";

const MIN_CREDIT_BUNDLE_AMOUNT = 50;
const CREDITS_SLIDER_STEP = 10;
const DEFAULT_SLIDER_MAX = 1000;
const CREDIT_SLIDER_MARK_VALUES = [
  50, 250, 500, 750, 1000, 1500, 2000,
] as const;

function formatUsd(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function resolveSliderMax(amount: number | null): number {
  if (amount === null || !Number.isFinite(amount)) {
    return DEFAULT_SLIDER_MAX;
  }
  const roundedToHundreds = Math.ceil(amount / 100) * 100;
  return Math.max(DEFAULT_SLIDER_MAX, roundedToHundreds);
}

function resolveTierForQuantity(
  quantity: number,
  tiers: CreditPricingTierContext[]
): CreditPricingTierContext | null {
  for (const tier of tiers) {
    if (quantity < tier.minQuantity) continue;
    if (tier.maxQuantity === null || quantity < tier.maxQuantity) {
      return tier;
    }
  }
  return null;
}

/** Strip request IDs and "Card error:" prefix; return a user-friendly payment error message. */
function formatPaymentErrorMessage(
  raw: string | null | undefined,
  defaultMessage: string
): string {
  if (!raw || typeof raw !== "string") return defaultMessage;
  let text = raw
    .replace(/^Card error:\s*/i, "")
    .replace(/Request req_\w+:\s*/gi, "")
    .trim();
  if (!text) return defaultMessage;
  // Use Stripe's message as-is if it's already short and clear; otherwise append guidance.
  const lower = text.toLowerCase();
  if (lower.includes("declined"))
    return "Your card was declined. Please try a different card or contact your bank.";
  if (lower.includes("insufficient funds"))
    return "Your card has insufficient funds. Please try a different card.";
  if (lower.includes("expir") || lower.includes("invalid"))
    return "Your card details appear invalid. Please check and try again.";
  if (lower.includes("incorrect") || lower.includes("wrong"))
    return "Your card details appear incorrect. Please check and try again.";
  if (text.length > 120) return defaultMessage;
  return text;
}

type PurchasePhase = "idle" | "submitting" | "success" | "error";

interface PurchaseCreditsBundleModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  commitmentDetails?: ContractCommitmentBillingDetails | null;
}

export const PurchaseCreditsBundleModal = ({
  opened,
  onClose,
  onSuccess,
  commitmentDetails,
}: PurchaseCreditsBundleModalProps) => {
  const baseCommittedCredits = commitmentDetails?.committedCredits ?? 0;
  const baseUnitPriceCents = commitmentDetails?.unitPriceCents ?? 0;
  const [amount, setAmount] = useState<number | null>(MIN_CREDIT_BUNDLE_AMOUNT);
  const [phase, setPhase] = useState<PurchasePhase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [hasAcceptedAgreementUpdate, setHasAcceptedAgreementUpdate] =
    useState<boolean>(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [pricingContext, setPricingContext] = useState<{
    minPurchaseQuantity: number;
    currentTotalCommittedUnits: number;
    currentCommittedValueCents: number;
    tiers: CreditPricingTierContext[];
  } | null>(null);

  const minimumAmount = Math.max(
    MIN_CREDIT_BUNDLE_AMOUNT,
    pricingContext?.minPurchaseQuantity ?? MIN_CREDIT_BUNDLE_AMOUNT
  );

  const sliderMax = resolveSliderMax(amount);
  const sliderMarks = CREDIT_SLIDER_MARK_VALUES.filter(
    (value) => value >= minimumAmount && value <= sliderMax
  ).map((value) => ({ value, label: String(value) }));
  const currentCommittedUnits =
    pricingContext?.currentTotalCommittedUnits ?? baseCommittedCredits;
  const currentSubtotalCents =
    pricingContext?.currentCommittedValueCents ??
    commitmentDetails?.totalCommittedValueCents ??
    currentCommittedUnits * baseUnitPriceCents;
  const currentUnitPriceCents =
    currentCommittedUnits > 0
      ? Math.round(currentSubtotalCents / currentCommittedUnits)
      : baseUnitPriceCents;
  const preview = useMemo(() => {
    const safeAmount = amount ?? minimumAmount;
    const updatedQuantity = currentCommittedUnits + safeAmount;
    const matchedTier = resolveTierForQuantity(
      safeAmount,
      pricingContext?.tiers ?? []
    );
    const unitPriceCents = matchedTier?.unitPriceCents ?? baseUnitPriceCents;
    const subtotalCents = safeAmount * unitPriceCents;
    return {
      subtotalCents,
      unitPriceCents,
      updatedCommittedUnits: updatedQuantity,
      updatedSubtotalCents: currentSubtotalCents + subtotalCents,
    };
  }, [
    amount,
    minimumAmount,
    currentCommittedUnits,
    pricingContext?.tiers,
    baseUnitPriceCents,
    currentSubtotalCents,
  ]);

  const resetState = useCallback(() => {
    setPhase("idle");
    setError(null);
    setAmount(minimumAmount);
    setHasAcceptedAgreementUpdate(false);
  }, [minimumAmount]);

  useEffect(() => {
    if (opened) {
      resetState();
    }
  }, [opened, resetState]);

  useEffect(() => {
    if (!opened) {
      return;
    }
    let ignore = false;
    const loadPricingContext = async () => {
      setContextLoading(true);
      try {
        const context = await getCreditBundlePricingContext();
        if (ignore) return;
        setPricingContext({
          minPurchaseQuantity: context.minPurchaseQuantity,
          currentTotalCommittedUnits: context.currentTotalCommittedUnits,
          currentCommittedValueCents: context.currentCommittedValueCents,
          tiers: context.tiers,
        });
        setAmount((current) => {
          if (current === null || current < context.minPurchaseQuantity) {
            return context.minPurchaseQuantity;
          }
          return current;
        });
      } catch {
        if (ignore) return;
        setPricingContext(null);
      } finally {
        if (!ignore) setContextLoading(false);
      }
    };
    loadPricingContext();
    return () => {
      ignore = true;
    };
  }, [opened]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleSubmit = useCallback(async () => {
    if (
      amount === null ||
      phase === "submitting" ||
      !hasAcceptedAgreementUpdate
    )
      return;

    setPhase("submitting");
    setError(null);

    try {
      await purchaseCreditBundle(amount, crypto.randomUUID());
      setPhase("success");
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : null;
      setPhase("error");
      setError(
        formatPaymentErrorMessage(
          msg,
          "Purchase failed. Please add or update your payment method in Billing and try again."
        )
      );
    }
  }, [amount, handleClose, hasAcceptedAgreementUpdate, onSuccess, phase]);

  const isBusy = phase === "submitting";

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text size="xl" fw={600}>
          Increase Planned Usage
        </Text>
      }
      size="lg"
      closeOnClickOutside={!isBusy}
      closeOnEscape={!isBusy}
      radius="lg"
      padding="xl"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Choose how many returns you’d like to add. The price for this increase
          uses the rate for this purchase size only. Your existing planned usage
          keeps its own rates.
        </Text>

        <Stack gap={0}>
          <Text size="sm" fw={500}>
            Returns to Add
          </Text>
          <Group gap="lg" align="flex-start" justify="center" wrap="nowrap">
            <Box w="100%" mt="md">
              <Slider
                min={minimumAmount}
                max={sliderMax}
                step={CREDITS_SLIDER_STEP}
                value={amount ?? minimumAmount}
                onChange={(value) => setAmount(value)}
                marks={sliderMarks}
                label={(value) => `${value} returns`}
                thumbSize={22}
                mb="lg"
                disabled={isBusy}
              />
            </Box>
            <NumberInput
              value={amount ?? undefined}
              min={minimumAmount}
              step={CREDITS_SLIDER_STEP}
              clampBehavior="strict"
              allowDecimal={false}
              thousandSeparator=","
              onChange={(value) =>
                setAmount(
                  typeof value === "number" ? value : Number(value) || null
                )
              }
              disabled={isBusy}
              w={170}
              label={
                <Text size="xs" c="dimmed">
                  Returns to Add
                </Text>
              }
            />
          </Group>
        </Stack>

        <Card withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
          <Stack gap={0}>
            <Group
              gap={0}
              wrap="nowrap"
              align="stretch"
              style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
            >
              <Box p="md" style={{ flex: 1 }} px="xl">
                <Stack gap={8}>
                  <Text fw={600}>Current plan</Text>
                  <Group justify="space-between" gap="sm" wrap="nowrap">
                    <Stack gap={0}>
                      <Text size="sm" fw={500}>
                        {currentCommittedUnits} Returns
                      </Text>
                      <Text size="10px" c="dimmed">
                        at {formatUsd(currentSubtotalCents)}
                      </Text>
                    </Stack>
                    <Text fw={700} size="lg">
                      {formatUsd(currentUnitPriceCents)}{" "}
                      <Text size="xs" c="dimmed" span>
                        /return
                      </Text>
                    </Text>
                  </Group>
                  <Text size="8px" c="dimmed" mt="xs">
                    * Effective rate based on current total planned usage.
                  </Text>
                </Stack>
              </Box>
              <Box
                p="md"
                px="xl"
                style={{
                  flex: 1,
                  borderLeft: "1px solid var(--mantine-color-gray-3)",
                  background: "var(--mantine-color-green-0)",
                  position: "relative",
                }}
              >
                <Box
                  style={{
                    position: "absolute",
                    left: -16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border: "1px solid var(--mantine-color-gray-3)",
                    background: "var(--mantine-color-white)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconArrowRight size={16} />
                </Box>
                <Stack gap={8}>
                  <Text fw={600} c="green.8">
                    Adding Now
                  </Text>
                  <Group justify="space-between" gap="sm" wrap="nowrap">
                    <Stack gap={0}>
                      <Group
                        gap={1}
                        align="center"
                        style={{ color: "var(--mantine-color-green-8)" }}
                      >
                        <IconLibraryPlus size={16} />
                        <Text size="sm" fw={500} c="green.8">
                          {amount ?? 0} returns
                        </Text>
                      </Group>
                      <Text size="10px" c="dimmed">
                        at {formatUsd(preview.subtotalCents)}
                      </Text>
                    </Stack>
                    <Text fw={700} size="lg" c="green.8">
                      {formatUsd(preview.unitPriceCents)}{" "}
                      <Text size="xs" c="dimmed" span>
                        /return
                      </Text>
                    </Text>
                  </Group>
                </Stack>
              </Box>
            </Group>
            <Group
              gap={0}
              wrap="nowrap"
              align="stretch"
              style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
            >
              <Group
                p="md"
                justify="space-between"
                style={{
                  flex: 1,
                  background: "var(--mantine-color-blue-0)",
                }}
                wrap="nowrap"
              >
                <Group gap="xs" align="center">
                  <Text fw={800} c="blue.8">
                    New Planned Usage
                  </Text>
                  <IconRosetteDiscountCheckFilled
                    size={16}
                    style={{ color: "var(--mantine-color-blue-8)" }}
                  />
                </Group>
                <Text fw={800} size="lg" c="blue.8">
                  {preview.updatedCommittedUnits.toLocaleString()}{" "}
                  <Text size="xs" c="blue.8" span>
                    returns
                  </Text>
                </Text>
              </Group>
            </Group>
          </Stack>
        </Card>

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            By confirming, you agree to update your original usage agreement to
            reflect your new planned usage. The rate for this increase is based
            on the number of returns you add in this step; previously committed
            returns keep their existing rates. Any added returns follow the same
            billing date and contract term as your original agreement.
          </Text>
          <Checkbox
            checked={hasAcceptedAgreementUpdate}
            onChange={(event) =>
              setHasAcceptedAgreementUpdate(event.currentTarget.checked)
            }
            label="I accept the terms and conditions"
            size="xs"
          />
        </Stack>

        <Button
          leftSection={isBusy ? <Loader size="sm" /> : ""}
          onClick={handleSubmit}
          disabled={
            amount === null ||
            amount < minimumAmount ||
            isBusy ||
            contextLoading ||
            !hasAcceptedAgreementUpdate
          }
          size="lg"
          variant="light"
        >
          {isBusy ? "Processing..." : `Increase by ${amount ?? 0} returns`}
        </Button>
      </Stack>
    </Modal>
  );
};
